import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AuthShell from "@/components/layout/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LOGIN } from "@/constants/testIds";
import { Loader2, AlertCircle } from "lucide-react";
import { Brand } from "@/components/Brand";

export default function SignIn() {
  const { login, errMsg } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      const isAdmin = user.role === "admin" || user.role === "superadmin";
      const dest = location.state?.from || (isAdmin ? "/admin/reviews" : "/organizer/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={<>Sign in to <Brand /></>}
      subtitle="Access your organizer dashboard, campaigns, and payouts."
      footer={<>New to <Brand />? <Link to="/sign-up" data-testid={LOGIN.registerLink} className="font-semibold text-ff-red hover:underline">Create an account</Link></>}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="signin-form">
        {error && (
          <Alert variant="destructive" data-testid="signin-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required data-testid={LOGIN.emailInput} value={email}
            onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="you@example.com"
            autoComplete="email" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" data-testid={LOGIN.forgotPasswordLink} className="text-xs font-semibold text-ff-red hover:underline">
              Forgot password?
            </Link>
          </div>
          <Input id="password" type="password" required data-testid={LOGIN.passwordInput} value={password}
            onChange={(e) => setPassword(e.target.value)} className="mt-1.5" autoComplete="current-password" />
        </div>
        <Button type="submit" disabled={loading} data-testid={LOGIN.submitButton}
          className="w-full bg-ff-blue font-semibold text-white hover:bg-ff-blue/90">
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in</> : "Sign In"}
        </Button>
      </form>
      <div className="mt-5 rounded-lg border border-ff-border bg-[#F3F4F6] p-3 text-xs text-ff-dark">
        <span className="font-semibold">Demo organizer:</span> organizer@fightfund.org / Organizer123!
      </div>
    </AuthShell>
  );
}
